import i18n from '@/utils/i18n';
import React, { useRef, useState } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { Key } from '@platform/shortcuts/utils/keyboard-platform';
import {
  NativeSyntheticEvent,
  TextInput,
  TextInputKeyPressEventData,
  View,
} from 'react-native';
import { Typography } from '@usepepper/ui/atoms/typography';

interface QuoteNameEditorProps {
  onNameChange: (name: string) => void;
  quoteName: string;
}

/**
 * Page title + click-to-edit inline input for the quote name.
 *
 * The edit flow saves on blur (trimmed, `onNameChange` called) and cancels
 * on Escape (local input state reverts, no callback). An empty or
 * whitespace-only blur is treated as a cancel.
 */
export const QuoteNameEditor: React.FC<QuoteNameEditorProps> = ({
  onNameChange,
  quoteName,
}) => {
  const [styles, theme] = useStyles();
  const [isEditing, setIsEditing] = useState(false);
  const [localName, setLocalName] = useState(quoteName);
  const inputRef = useRef<TextInput>(null);
  // Set when Escape cancels an edit so onBlur skips the dispatch.
  const isCancelingRef = useRef(false);

  const handleBlur = () => {
    setIsEditing(false);
    if (isCancelingRef.current) {
      isCancelingRef.current = false;
      setLocalName(quoteName);
      return;
    }
    const trimmed = localName.trim();
    if (trimmed) {
      onNameChange(trimmed);
    } else {
      setLocalName(quoteName);
    }
  };

  const handleKeyPress = (
    e: NativeSyntheticEvent<TextInputKeyPressEventData>,
  ) => {
    if (e.nativeEvent.key === Key.Escape) {
      isCancelingRef.current = true;
      inputRef.current?.blur();
    }
  };

  return (
    <View style={styles.container}>
      <Typography
        color={theme.sk.color.text.primary}
        variant="paragraph-md-600"
      >
        {i18n.t('sales_portal.price_quotes.header_title')}
      </Typography>
      <Typography color={theme.sk.color.text.tertiary} variant="paragraph-md">
        /
      </Typography>
      {isEditing ? (
        <TextInput
          accessibilityLabel={i18n.t('sales_portal.price_quotes.quote_name')}
          autoFocus
          onBlur={handleBlur}
          onChangeText={setLocalName}
          onKeyPress={handleKeyPress}
          ref={inputRef}
          selectTextOnFocus
          style={styles.nameInput}
          value={localName}
        />
      ) : (
        <Typography
          color={theme.sk.color.text.secondary}
          onPress={() => {
            setLocalName(quoteName);
            setIsEditing(true);
          }}
          variant="paragraph-md"
        >
          {quoteName ||
            i18n.t('sales_portal.price_quotes.quote_name_placeholder')}
        </Typography>
      )}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
  },
  nameInput: {
    borderColor: theme.sk.color.border.input.active,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    color: theme.sk.color.text.primary,
    fontFamily: theme.sk.typography.paragraph.md.fontFamily,
    fontSize: theme.sk.typography.paragraph.md.fontSize,
    minWidth: 200,
    paddingHorizontal: theme.sk.spacing.spaceSmall,
    paddingVertical: theme.sk.spacing.spaceXsmall,
  },
}));
